import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import Box from '@mui/material/Box';
import { Tooltip, IconButton, CircularProgress } from "@mui/joy";
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import { getDocument, TextLayer } from 'pdfjs-dist';
import { initPdfJsWorker } from '../../lib/pdfjs/setupWorker';

initPdfJsWorker();

const imagesType = ["jpeg", "jpg", "png"];
const BBOX_PADDING_PERCENT = 2;

const BboxOverlay = ({ bbox, isHighlighted, shouldPulse, setOverlayRef }) => {
  const [hovered, setHovered] = useState(false);
  const show = isHighlighted || hovered;

  return (
    <Box
      ref={setOverlayRef}
      data-testid={`bbox-overlay-${bbox.key}`}
      data-highlighted={show ? "true" : "false"}
      data-pulsing={shouldPulse ? "true" : "false"}
      data-flashing={shouldPulse ? "true" : "false"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "absolute",
        left: `${bbox.left}%`,
        top: `${bbox.top}%`,
        width: `${bbox.width}%`,
        height: `${bbox.height}%`,
        border: show ? "1.5px solid red" : "1px solid red",
        backgroundColor: "transparent",
        pointerEvents: "auto",
        boxSizing: "border-box",
        transition: "all 0.15s ease",
        transformOrigin: "center",
        animation: shouldPulse
          ? "bboxPulseScale 1.2s ease-in-out, bboxFlash 1.2s ease-in-out"
          : "none",
        "@keyframes bboxPulseScale": {
          "0%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.08)" },
          "100%": { transform: "scale(1)" },
        },
        "@keyframes bboxFlash": {
          "0%, 100%": { borderColor: "rgba(255, 0, 0, 1)", opacity: 1 },
          "15%, 45%, 75%": { borderColor: "rgba(255, 0, 0, 0.35)", opacity: 0.5 },
          "30%, 60%, 90%": { borderColor: "rgba(255, 0, 0, 1)", opacity: 1 },
        },
      }}
    >
      {show && (
        <Box
          sx={{
            position: "absolute",
            top: -24,
            left: 0,
            backgroundColor: "red",
            color: "white",
            fontSize: 11,
            fontWeight: 600,
            px: 0.75,
            py: 0.25,
            borderRadius: "4px 4px 0 0",
            whiteSpace: "nowrap",
            lineHeight: 1.3,
            pointerEvents: "none",
          }}
        >
          {bbox.key}
        </Box>
      )}
    </Box>
  );
};

const DocumentPreview = ({
  id,
  documentUrl,
  documentType,
  invertColors,
  onToggleInvert,
  hasDocument,
  fieldBboxes,
  documentPage,
  onPageChange,
  hoveredField,
  shownFieldKey,
  showTriggerId,
}) => {
  const [naturalSize, setNaturalSize] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageSizes, setPageSizes] = useState({});  // {pageNum: {width, height}} at display scale
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activePulseField, setActivePulseField] = useState(null);
  const pdfDocRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const bboxRefs = useRef({});
  const pageContainerRefs = useRef({});
  const canvasRefs = useRef({});
  const textLayerRefs = useRef({});

  const handleImageLoad = useCallback((e) => {
    setNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
  }, []);

  // Load PDF document
  useEffect(() => {
    if (documentType !== "pdf" || !hasDocument) return;

    let cancelled = false;
    setPdfLoading(true);
    setPdfDoc(null);
    setNumPages(0);
    setPageSizes({});

    const loadPdf = async () => {
      try {
        const rawUrl = documentUrl.replace(/#.*$/, '');
        if (!rawUrl) return;

        if (!pdfDocRef.current || pdfDocRef.current._url !== rawUrl) {
          if (pdfDocRef.current) pdfDocRef.current.destroy();

          let loadingTask;
          if (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:')) {
            loadingTask = getDocument(rawUrl);
          } else {
            const response = await fetch(rawUrl);
            if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
            const arrayBuffer = await response.arrayBuffer();
            loadingTask = getDocument({ data: arrayBuffer });
          }

          const pdf = await loadingTask.promise;
          if (cancelled) { pdf.destroy(); return; }
          pdf._url = rawUrl;
          pdfDocRef.current = pdf;
        }

        if (cancelled) return;
        const pdf = pdfDocRef.current;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
      } catch (err) {
        console.error('Failed to load PDF:', err);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };

    loadPdf();
    return () => { cancelled = true; };
  }, [documentUrl, documentType, hasDocument]);

  // Render all pages to canvas + TextLayer once pdfDoc and canvas DOM nodes are ready
  useEffect(() => {
    if (!pdfDoc || numPages === 0) return;

    let cancelled = false;

    const renderAll = async () => {
      const sizes = {};
      for (let i = 1; i <= numPages; i++) {
        if (cancelled) return;

        const canvasEl = canvasRefs.current[i];
        const textLayerEl = textLayerRefs.current[i];
        if (!canvasEl) continue;

        try {
          const pdfPage = await pdfDoc.getPage(i);
          if (cancelled) return;

          const scale = 1.5;
          const viewport = pdfPage.getViewport({ scale });
          const displayViewport = pdfPage.getViewport({ scale: 1 });

          canvasEl.width = viewport.width;
          canvasEl.height = viewport.height;
          canvasEl.style.width = `${displayViewport.width}px`;
          canvasEl.style.height = `${displayViewport.height}px`;

          await pdfPage.render({ canvasContext: canvasEl.getContext('2d'), viewport }).promise;
          if (cancelled) return;

          if (textLayerEl) {
            const textContent = await pdfPage.getTextContent();
            if (cancelled) return;
            textLayerEl.replaceChildren();
            textLayerEl.style.width = `${displayViewport.width}px`;
            textLayerEl.style.height = `${displayViewport.height}px`;
            const tl = new TextLayer({
              textContentSource: textContent,
              container: textLayerEl,
              viewport: displayViewport,
            });
            await tl.render();
          }

          sizes[i] = { width: displayViewport.width, height: displayViewport.height };
        } catch (err) {
          console.error(`Failed to render page ${i}:`, err);
        }
      }
      if (!cancelled) setPageSizes(sizes);
    };

    renderAll();
    return () => { cancelled = true; };
  }, [pdfDoc, numPages]);

  // Cleanup pdf doc on unmount
  useEffect(() => {
    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, []);

  // Group bboxes by page number
  const bboxesByPage = useMemo(() => {
    if (!fieldBboxes) return {};
    const result = {};
    Object.entries(fieldBboxes).forEach(([key, v]) => {
      if (!v?.bbox) return;
      const pageNum = v.page;
      if (!result[pageNum]) result[pageNum] = [];
      result[pageNum].push({ key, v });
    });
    return result;
  }, [fieldBboxes]);

  // Compute rendered bboxes for a given page and its display pixel size
  const computeBboxesForPage = useCallback((pageNum, pageSize) => {
    const entries = bboxesByPage[pageNum] || [];
    return entries.map(({ key, v }) => {
      const docSize = (v.image_width && v.image_height)
        ? { width: v.image_width, height: v.image_height }
        : pageSize;
      if (!docSize) return null;
      const rawLeft = (v.bbox.x / docSize.width) * 100;
      const rawTop = (v.bbox.y / docSize.height) * 100;
      const rawWidth = (v.bbox.width / docSize.width) * 100;
      const rawHeight = (v.bbox.height / docSize.height) * 100;
      const paddedLeft = Math.max(rawLeft - BBOX_PADDING_PERCENT, 0);
      const paddedTop = Math.max(rawTop - BBOX_PADDING_PERCENT, 0);
      const paddedRight = Math.min(rawLeft + rawWidth + BBOX_PADDING_PERCENT, 100);
      const paddedBottom = Math.min(rawTop + rawHeight + BBOX_PADDING_PERCENT, 100);
      return {
        key,
        left: paddedLeft,
        top: paddedTop,
        width: Math.max(paddedRight - paddedLeft, 0),
        height: Math.max(paddedBottom - paddedTop, 0),
      };
    }).filter(Boolean);
  }, [bboxesByPage]);

  // Bboxes for single-image documents
  const imageBboxes = useMemo(() => {
    if (!fieldBboxes || !naturalSize) return [];
    return Object.entries(fieldBboxes)
      .filter(([, v]) => v?.bbox)
      .map(([key, v]) => {
        const docSize = (v.image_width && v.image_height)
          ? { width: v.image_width, height: v.image_height }
          : naturalSize;
        if (!docSize) return null;
        const rawLeft = (v.bbox.x / docSize.width) * 100;
        const rawTop = (v.bbox.y / docSize.height) * 100;
        const rawWidth = (v.bbox.width / docSize.width) * 100;
        const rawHeight = (v.bbox.height / docSize.height) * 100;
        const paddedLeft = Math.max(rawLeft - BBOX_PADDING_PERCENT, 0);
        const paddedTop = Math.max(rawTop - BBOX_PADDING_PERCENT, 0);
        const paddedRight = Math.min(rawLeft + rawWidth + BBOX_PADDING_PERCENT, 100);
        const paddedBottom = Math.min(rawTop + rawHeight + BBOX_PADDING_PERCENT, 100);
        return {
          key,
          left: paddedLeft,
          top: paddedTop,
          width: Math.max(paddedRight - paddedLeft, 0),
          height: Math.max(paddedBottom - paddedTop, 0),
        };
      })
      .filter(Boolean);
  }, [fieldBboxes, naturalSize]);

  const setBboxRef = useCallback((fieldKey, node) => {
    if (node) {
      bboxRefs.current[fieldKey] = node;
      return;
    }
    delete bboxRefs.current[fieldKey];
  }, []);

  useEffect(() => {
    if (!shownFieldKey || showTriggerId === 0) return undefined;

    setActivePulseField(shownFieldKey);
    const timeoutId = window.setTimeout(() => {
      setActivePulseField(null);
    }, 1200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shownFieldKey, showTriggerId]);

  useEffect(() => {
    if (!shownFieldKey || showTriggerId === 0) return;

    const scrollContainer = scrollContainerRef.current;
    const overlayNode = bboxRefs.current[shownFieldKey];
    if (!scrollContainer || !overlayNode) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const overlayRect = overlayNode.getBoundingClientRect();

    const targetCenterLeft =
      overlayRect.left - containerRect.left + scrollContainer.scrollLeft + (overlayRect.width / 2);
    const targetCenterTop =
      overlayRect.top - containerRect.top + scrollContainer.scrollTop + (overlayRect.height / 2);

    const maxScrollLeft = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0);
    const maxScrollTop = Math.max(scrollContainer.scrollHeight - scrollContainer.clientHeight, 0);

    const targetLeft = Math.min(
      Math.max(targetCenterLeft - (scrollContainer.clientWidth / 2), 0),
      maxScrollLeft
    );
    const targetTop = Math.min(
      Math.max(targetCenterTop - (scrollContainer.clientHeight / 2), 0),
      maxScrollTop
    );

    if (typeof scrollContainer.scrollTo === "function") {
      scrollContainer.scrollTo({ top: targetTop, left: targetLeft, behavior: "smooth" });
      return;
    }

    scrollContainer.scrollTop = targetTop;
    scrollContainer.scrollLeft = targetLeft;
  }, [shownFieldKey, showTriggerId]);

  const renderPdfPages = () => (
    <Box
      ref={scrollContainerRef}
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        p: 2,
        bgcolor: '#e8e8e8',
      }}
    >
      {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
        const bboxesOnPage = computeBboxesForPage(pageNum, pageSizes[pageNum]);
        return (
          <Box
            key={pageNum}
            ref={(node) => {
              if (node) pageContainerRefs.current[pageNum] = node;
              else delete pageContainerRefs.current[pageNum];
            }}
            sx={{
              position: "relative",
              display: "inline-block",
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              bgcolor: 'white',
            }}
          >
            <canvas
              ref={(el) => { canvasRefs.current[pageNum] = el; }}
              style={{ display: 'block', filter: invertColors ? 'invert(1)' : 'none' }}
            />
            <Box
              ref={(el) => { textLayerRefs.current[pageNum] = el; }}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                overflow: 'hidden',
                opacity: 0.2,
                lineHeight: 1,
                userSelect: 'text',
                '& span': {
                  position: 'absolute',
                  whiteSpace: 'pre',
                  transformOrigin: '0% 0%',
                  color: 'transparent',
                  cursor: 'text',
                  '&::selection': {
                    backgroundColor: 'rgba(0, 100, 255, 0.3)',
                    color: 'transparent',
                  },
                },
              }}
            />
            {bboxesOnPage.map((bbox) => (
              <BboxOverlay
                key={bbox.key}
                bbox={bbox}
                isHighlighted={hoveredField === bbox.key || shownFieldKey === bbox.key}
                shouldPulse={activePulseField === bbox.key}
                setOverlayRef={(node) => setBboxRef(bbox.key, node)}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );

  const renderImageWithOverlays = () => (
    <Box
      ref={scrollContainerRef}
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ position: "relative", maxWidth: "100%", display: "inline-block" }}>
        <img
          src={documentUrl}
          style={{
            maxWidth: "100%",
            height: "auto",
            display: "block",
            filter: invertColors ? "invert(1)" : "none",
          }}
          alt="Document"
          loading="lazy"
          decoding="async"
          onLoad={handleImageLoad}
        />
        {imageBboxes.map((bbox) => (
          <BboxOverlay
            key={bbox.key}
            bbox={bbox}
            isHighlighted={hoveredField === bbox.key || shownFieldKey === bbox.key}
            shouldPulse={activePulseField === bbox.key}
            setOverlayRef={(node) => setBboxRef(bbox.key, node)}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 0,
      }}
    >
      {hasDocument && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            px: 1,
            pt: 1,
            pb: 0.75,
            flexShrink: 0,
          }}
        >
          <Tooltip title={invertColors ? "Normal colors" : "Invert colors"} arrow>
            <IconButton
              onClick={onToggleInvert}
              variant={invertColors ? "solid" : "outlined"}
              color="primary"
              size="sm"
              aria-label={invertColors ? "Normal colors" : "Invert colors"}
              sx={{ bgcolor: 'background.surface' }}
            >
              <InvertColorsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {hasDocument ? (
          documentType === "pdf" ? (
            pdfLoading && numPages === 0 ? (
              <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
                <CircularProgress size="lg" />
              </Box>
            ) : (
              renderPdfPages()
            )
          ) : imagesType.includes(documentType) ? (
            renderImageWithOverlays()
          ) : null
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
            <Typography level="body-sm" color="neutral">
              No document available
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default DocumentPreview;
